import { useEffect, useRef, useState } from "react";

import { Checkout as CiclCheckout } from "@comparaonline/cicl-funnel-schema";
import { Checkout as CicoCheckout } from "@comparaonline/cico-funnel-schema";
import { CicoForm, CiclForm } from "@comparaonline/quote-forms-schema/dist/schema";
import { Offer } from "@comparaonline/offers-models";

import { useFirebase } from "../../../../pages/FirebaseProvider";

import { FunnelName, FunnelCollectionName } from "../constants";
import { getFunnelCollection } from "../store/funnel";

type Checkout = Partial<CiclCheckout> | Partial<CicoCheckout>;
type QuoteForm = CiclForm | CicoForm;

export type UseFunnelData = (
  funnelName: FunnelName,
  formId: string
) => {
  error: boolean;
  checkout: Checkout;
  loadingCheckout: boolean;
  offer: Offer;
  loadingOffer: boolean;
  quoteForm: QuoteForm;
  loadingQuote: boolean;
};

interface State {
  checkout?: Partial<Checkout>;
  error: boolean;
  offer?: Offer;
  quoteForm?: QuoteForm;
}

export const useFunnelData: UseFunnelData = (funnelName, formId) => {
  const firebase = useFirebase();
  const isCancelled = useRef(false);
  const [{ checkout, error, offer, quoteForm }, setData] = useState<State>({ error: false });

  const quoteCollectionName =
    funnelName === FunnelName.Cico ? FunnelCollectionName.Quote : FunnelCollectionName.QuoteForm;

  const getCollection = (name: FunnelCollectionName) =>
    getFunnelCollection({ documentId: formId, firebase, funnelName, name });

  useEffect(() => {
    if (!formId || !firebase) return;

    Promise.all([
      getCollection(FunnelCollectionName.Checkout),
      getCollection(FunnelCollectionName.Offer),
      getCollection(quoteCollectionName)
    ])
      .then(([checkoutData, offerData, quoteFormData]) => {
        if (!isCancelled.current) {
          setData({
            checkout: checkoutData,
            error: false,
            offer: offerData,
            quoteForm: quoteFormData
          });
        }
      })
      .catch(() => {
        if (!isCancelled.current) setData({ error: true });
      });

    return () => {
      isCancelled.current = true;
    };
  }, [formId, firebase]);

  return {
    error,
    checkout,
    loadingCheckout: !checkout,
    offer,
    loadingOffer: !offer,
    quoteForm,
    loadingQuote: !quoteForm
  };
};
